import { StatusCode } from "api/protocols";
import { Badge } from "components/badge/Badge";
import Card from "components/card/Card";
import Page from "components/page/Page";
import Sheet from "components/sheet/Sheet";
import { capitalize, customerErrorMessage, uppercase } from "helpers/Helpers";
import { LocaleCurrency } from "helpers/Locale";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { IUIActions } from "redux/actions/ui";
import { ICustomer } from "redux/reducers/models/customer";
import "./giftCard.scss";
import ReactDOM from "react-dom";
import { IOrder } from "redux/reducers/models/order";
import moment from "moment";

import Popup from "components/popup/Popup";
import Input from "components/form/input/Input";

import { useTranslation } from "react-i18next";

import { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import {
  EmailRainCheckReceipt,
  GetRainCheckReceipt,
  GetRainChecks,
  IRainCheck,
  PutRainCheck,
} from "api/rpc/2024-04/facilityAdmin/payment/rainCheck";
import ChangeCustomer from "components/changeCustomer/ChangeCustomer";
import { GetCustomer, PostCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetOrder } from "api/rpc/2024-04/facilityAdmin/order/order";

interface IRainCheckState {
  rainCheck: IRainCheck;
  currentCustomer: ICustomer;
  order: IOrder;
  showChangeCustomerModal: boolean;
  customerQuery: string;
  selectedCustomer: ICustomer;
  customerSearching: boolean;
  customerSearchResult: Array<ICustomer>;
  showRedeemModal: boolean;
  showPrintModal: boolean;
  showEmailModal: boolean;
}

interface INewCustomerState {
  showNewCustomerModal: boolean;
}

interface IProps {
  uiActions: IUIActions;
}

interface IEmailState {
  modalVisible: boolean;
  email: string;
}

export default function RainCheck(props: IProps) {
  const { uiActions } = props;
  const history = useHistory();
  const { t } = useTranslation();
  const { rainCheckId } = useParams<{ rainCheckId: string }>();
  const [state, setState] = useState<IRainCheckState>({
    rainCheck: null,
    currentCustomer: null,
    order: null,
    showChangeCustomerModal: false,
    customerQuery: "",
    selectedCustomer: null,
    customerSearching: false,
    customerSearchResult: [],
    showRedeemModal: false,
    showEmailModal: false,
    showPrintModal: false,
  });

  const [emailState, setEmailState] = useState<IEmailState>({
    modalVisible: false,
    email: "",
  });

  const [newCustomerState, setNewCustomerState] = useState<INewCustomerState>({
    showNewCustomerModal: false,
  });

  useEffect(() => {
    void loadRainCheck();
  }, []);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (async () => {
          if (state.customerQuery !== "") {
            try {
              setState(prevState => ({ ...prevState, customerSearching: true }));
              const customers = await searchCustomer();
              if (mounted) {
                setState(prevState => ({ ...prevState, customerSearchResult: customers, customerSearching: false }));
              }
            } catch (error) {
              console.log("err", error);
            }
            return;
          } else {
            setState(prevState => ({ ...prevState, customerSearchResult: [], customerSearching: false }));
          }
        })();
      }, 1000);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [state.customerQuery]);

  async function searchCustomer() {
    const customerRes = await GetCustomer({ search: state.customerQuery }, false);
    if (customerRes.status !== StatusCode.OK) {
      return [];
    }
    return customerRes.data;
  }

  async function loadRainCheck() {
    uiActions.enqueue();

    const rainCheckRes = await GetRainChecks({ id: Number(rainCheckId) }, false);

    if (rainCheckRes.status !== StatusCode.OK || rainCheckRes?.data?.length === 0) {
      uiActions.showError(t("secure.facility.order.rain_check.001"));
      history.push("/admin/rainchecks");
      uiActions.dequeue();
      return;
    }
    const rainCheck = rainCheckRes.data[0];

    if (rainCheck?.customer_id) {
      await loadCustomer(rainCheck?.customer_id);
    }

    if (rainCheck?.order_id) {
      await loadOrder(rainCheck?.order_id);
    }

    uiActions.dequeue();
    setState(prevState => ({ ...prevState, rainCheck: rainCheckRes.data[0] }));
    setEmailState(prevState => ({ ...prevState, email: rainCheckRes.data[0].customer?.email }));
  }

  async function loadCustomer(customer_id: number) {
    const customerRes = await GetCustomer({ id: customer_id }, false);
    if (customerRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.order.rain_check.002"));
      return;
    }
    setState(prevState => ({ ...prevState, currentCustomer: customerRes.data[0] }));
  }

  async function loadOrder(orderId: number) {
    const orderRes = await GetOrder({ id: orderId }, false);
    if (orderRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.order.rain_check.003"));
      return;
    }
    setState(prevState => ({ ...prevState, order: orderRes.data[0] }));
  }

  function closeChangeCustomerModal() {
    setState(prevState => ({
      ...prevState,
      showChangeCustomerModal: false,
      customerQuery: "",
      customerSearchResult: [],
      selectedCustomer: null,
    }));
  }

  function handleCustomerSearch(query: string) {
    setState(prevState => ({ ...prevState, customerQuery: query }));
  }

  function handleCustomerSelection(id: number, customer: ICustomer) {
    setState(prevState => ({ ...prevState, selectedCustomer: customer }));
  }

  async function assignCustomer() {
    const updateRainCheckRes = await PutRainCheck(
      { id: Number(rainCheckId), customer_id: state.selectedCustomer?.id },
      true,
    );
    if (updateRainCheckRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.order.rain_check.004"));
      return;
    }
    uiActions.showSuccess(t("secure.facility.order.rain_check.005"));
    void closeChangeCustomerModal();
    await loadRainCheck();
  }

  async function handleRainCheckRedeem() {
    const updateRainCheckRes = await PutRainCheck({ id: Number(rainCheckId), status: "redeemed" }, true);

    if (updateRainCheckRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.order.rain_check.006"));
      return;
    }

    uiActions.showSuccess(t("secure.facility.order.rain_check.007"));
    setState(prevState => ({ ...prevState, showRedeemModal: false }));
    await loadRainCheck();
  }

  async function createNewCustomer(customerInfo: ICustomerInfoState) {
    if (
      !customerInfo.firstName ||
      !customerInfo.lastName ||
      (!customerInfo.emailAddress && !customerInfo.phoneNumber)
    ) {
      uiActions.showError(t("secure.facility.order.rain_check.008"));
    }

    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber ?? null,
        email: customerInfo.emailAddress ?? null,
        send_welcome_email: customerInfo.sendWelcomeEmail,
      },
      true,
    );

    if (customerRes.status !== StatusCode.OK) {
      uiActions.showError(customerErrorMessage(t, customerRes?.data?.message));
      return;
    }
    ReactDOM.unstable_batchedUpdates(() => {
      setNewCustomerState(prevState => ({
        ...prevState,
        showNewCustomerModal: false,
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      }));
      setState(prevState => ({ ...prevState, selectedCustomer: customerRes.data }));
    });
    uiActions.showSuccess(t("secure.facility.order.rain_check.009"));
  }

  function openNewCustomerSheet() {
    setNewCustomerState(prevState => ({
      ...prevState,
      showNewCustomerModal: true,
    }));
  }

  function navigateToOrder() {
    if (state.rainCheck?.order_id) {
      history.push("/admin/order/" + String(state.rainCheck?.order_id));
    }
  }

  async function printReceipt() {
    if (state.rainCheck) {
      const rainCheckReceiptRes = await GetRainCheckReceipt({ id: state.rainCheck.id }, true);
      if (rainCheckReceiptRes.status !== StatusCode.OK) {
        uiActions.showError(rainCheckReceiptRes.message);
        return;
      } else {
        window.open().document.write(rainCheckReceiptRes.data);
      }
    }
  }

  async function emailReceipt() {
    const emailReceiptRes = await EmailRainCheckReceipt(
      { id: state?.rainCheck?.id, customer_email: emailState?.email },
      true,
    );
    if (emailReceiptRes?.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.order.rain_check.028"));
      return;
    }
    setEmailState(prevState => ({ ...prevState, modalVisible: false, email: "" }));
    uiActions.showSuccess(t("secure.facility.order.rain_check.029"));
  }

  function handleEmailInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setEmailState(prevState => ({ ...prevState, [id]: value }));
  }

  return (
    <>
      <Page
        narrow
        splitLayout
        title={t("secure.facility.order.rain_check.010")}
        subtitle={`•••• •••• ${state.rainCheck?.last4}`}
        breadcrumbs={[{ prefix: true, label: t("secure.facility.order.rain_check.011"), url: "/admin/rainchecks" }]}
        multipleActionDropdownAction={{
          label: t("secure.facility.order.rain_check.030"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("secure.facility.order.rain_check.012"),
                icon: "ticket",
                handler: () => setState(prevState => ({ ...prevState, showRedeemModal: true })),
              },
              {
                type: "handler",
                label: t("secure.facility.order.rain_check.031"),
                handler: () => printReceipt(),
                icon: "print",
              },
              {
                type: "handler",
                label: t("secure.facility.order.rain_check.032"),
                handler: () =>
                  setEmailState(prevState => ({
                    ...prevState,
                    modalVisible: true,
                    email: state.rainCheck.customer?.email,
                  })),
                icon: "envelope",
              },
            ],
          },
        }}
      >
        {state?.rainCheck && (
          <>
            <Page.Section twoThirds>
              {/* Rain Check Details Card*/}
              <Card title={t("secure.facility.order.rain_check.013")}>
                <Card.Section>
                  <div className="orders-giftcard">
                    <div className="giftcard-column">
                      <div className="giftcard-row">
                        <p className="giftcard-details-title">{t("secure.facility.order.rain_check.014")}</p>
                        <p className="giftcard-details-value">
                          <LocaleCurrency currency={state.rainCheck?.currency} amount={state.rainCheck?.balance} />
                        </p>
                      </div>
                      <div className="giftcard-row">
                        <p className="giftcard-details-title">{t("secure.facility.order.rain_check.015")}</p>
                        <p className="giftcard-details-value">{uppercase(state.rainCheck?.currency)}</p>
                      </div>
                      <div className="giftcard-row">
                        <p className="giftcard-details-title">{t("secure.facility.order.rain_check.016")}</p>
                        <Badge size="small" type="success">
                          {capitalize(state.rainCheck?.status)}
                        </Badge>
                      </div>
                      {state.rainCheck?.expiry_date ? (
                        <div className="giftcard-row">
                          <p className="giftcard-details-title">{t("Expiry Date")}</p>
                          <p className="giftcard-details-value">
                            {moment(state.rainCheck?.expiry_date).local().format("MMM DD, YYYY")}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Card.Section>
              </Card>
            </Page.Section>

            <Page.Section oneThird>
              <Card
                title={t("secure.facility.order.rain_check.017")}
                titleActions={[
                  {
                    action: () => setState(prevState => ({ ...prevState, showChangeCustomerModal: true })),
                    content: t("secure.facility.order.rain_check.018"),
                  },
                ]}
              >
                {/* Customer Card */}
                <Card.Section>
                  {state.currentCustomer ? (
                    <div>
                      <p className="text-lg">{state?.currentCustomer?.full_name}</p>
                      <p className="text-lg">{state?.currentCustomer?.email}</p>
                      <p className="text-lg">{state?.currentCustomer?.phone}</p>
                      <p className="text-lg">{state?.currentCustomer?.customer_type}</p>
                    </div>
                  ) : (
                    <p>{t("secure.facility.order.rain_check.019")}</p>
                  )}
                </Card.Section>
                {/* End Customer Card */}
              </Card>

              {state?.order && (
                <Card
                  title={`${t("secure.facility.order.rain_check.020")} ${state.order?.name}`}
                  titleActions={[
                    {
                      action: navigateToOrder,
                      content: t(t("secure.facility.order.rain_check.021")),
                    },
                  ]}
                >
                  {/* Rain Check Order Card */}
                  <Card.Section>
                    <div className="orders-giftcard">
                      <div className="giftcard-column">
                        <div className="giftcard-row">
                          <p className="giftcard-details-title">{"Financial Status"}</p>
                          <Badge size="small" type="success">
                            {capitalize(state?.order?.financial_status)}
                          </Badge>
                        </div>
                        <div className="giftcard-row">
                          <p className="giftcard-details-title">{t("Total")}</p>
                          <LocaleCurrency currency={state?.order?.currency} amount={state?.order?.total_price} />
                        </div>
                      </div>
                    </div>
                  </Card.Section>
                  {/* Rain Check Order Card End */}
                </Card>
              )}
            </Page.Section>
          </>
        )}
      </Page>

      <ChangeCustomer
        title={t("secure.facility.order.rain_check.033")}
        currentCustomerTitle={t("secure.facility.order.rain_check.034")}
        newCustomerTitle={t("secure.facility.order.rain_check.035")}
        okText={t("secure.facility.order.gift_card.028")}
        open={state.showChangeCustomerModal}
        changeCustomerState={state}
        searching={state.customerSearching}
        searchResults={state.customerSearchResult}
        newCustomerActive={newCustomerState.showNewCustomerModal}
        onCancel={closeChangeCustomerModal}
        onOk={assignCustomer}
        handleSearchQueryChange={handleCustomerSearch}
        handleCustomerSelection={handleCustomerSelection}
        handleRemoveSelectedCustomer={() =>
          setState(prevState => ({
            ...prevState,
            selectedCustomer: null,
            customerSearchResult: [],
            customerQuery: "",
          }))
        }
        handleCreateNewCustomer={createNewCustomer}
        handleCloseCreateNewCustomer={() =>
          setNewCustomerState(prevState => ({
            ...prevState,
            showNewCustomerModal: false,
          }))
        }
        handleNewCustomerActive={openNewCustomerSheet}
      />

      <Popup
        type="warning"
        open={state.showRedeemModal}
        title={t("secure.facility.order.rain_check.026")}
        description={t("secure.facility.order.rain_check.027")}
        closable
        onOk={handleRainCheckRedeem}
        onCancel={() => setState(prevState => ({ ...prevState, showRedeemModal: false }))}
        okText={t("secure.facility.order.rain_check.012")}
      />

      <Sheet
        title={t("secure.facility.order.rain_check.036")}
        open={emailState.modalVisible}
        size="small"
        stacked
        closable
        onCancel={() => setEmailState(prevState => ({ ...prevState, modalVisible: false, email: "" }))}
        onOk={emailReceipt}
        okText={t("secure.facility.order.rain_check.037")}
        okDisabled={!emailState.email ? true : false}
      >
        <Input
          placeholder={t("secure.facility.order.rain_check.032")}
          value={emailState.email}
          id="email"
          onChange={handleEmailInputChange}
        />
      </Sheet>
    </>
  );
}
